import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import type { AuthenticationResult } from '@azure/msal-browser'
import type { Msal } from '@base/types/msal'

let msalInstance: PublicClientApplication | null = null
let msalInitialized = false

const initializeMsalInstance = async ({ auth }: Msal) => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({ auth })
  }

  if (!msalInitialized) {
    try {
      await msalInstance.initialize()

      msalInitialized = true
    } catch (error) {
      console.error('MSAL instance initialization failed', error)
      throw error
    }
  }

  return msalInstance
}

const useMsal = (msal: Msal) => {
  const { scopes } = msal.auth || []

  const handleError = (error: unknown, message: string) => {
    console.error(message, error)
  }

  const handleRedirect = async () => {
    try {
      const msalInstance = await initializeMsalInstance(msal)

      await msalInstance.handleRedirectPromise()
    } catch (error) {
      handleError(error, 'An error occurred during redirect')
    }
  }

  const signIn = async (): Promise<AuthenticationResult | undefined> => {
    try {
      const msalInstance = await initializeMsalInstance(msal)

      if (msalInstance.getAllAccounts().length === 0 && !msalInstance.getActiveAccount()) {
        await handleRedirect()
        await msalInstance.loginRedirect({ scopes })
      }

      // Sign-in process is handled by redirect
      return undefined
    } catch (error) {
      handleError(error, 'An error occurred during sign-in')

      return undefined
    }
  }

  const acquireTokenSilent = async (): Promise<AuthenticationResult | undefined> => {
    const msalInstance = await initializeMsalInstance(msal)

    await handleRedirect()

    const accounts = msalInstance.getAllAccounts()

    if (!accounts.length) return await signIn()

    const request = {
      scopes,
      account: accounts[0]
    }

    try {
      const response = await msalInstance.acquireTokenSilent(request)

      return response?.accessToken ? response : await signIn()
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Interaction required, redirecting to acquire token interactively.')

        await msalInstance.acquireTokenRedirect(request)

        // Redirect will handle token acquisition
        return undefined
      } else {
        console.error('An error occurred during token acquisition', error)

        return await signIn()
      }
    }
  }

  return { acquireTokenSilent, handleRedirect, signIn }
}

export { useMsal }
